package com.swetdot.swetgram

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch
import androidx.lifecycle.viewmodel.compose.viewModel
import coil3.compose.setSingletonImageLoaderFactory
import com.swetdot.swetgram.data.ioDispatcher
import com.swetdot.swetgram.data.media.swetImageLoader
import com.swetdot.swetgram.ui.SwetgramApp
import com.swetdot.swetgram.ui.auth.AuthFlow
import com.swetdot.swetgram.ui.auth.AuthViewModel
import com.swetdot.swetgram.ui.auth.VerifyEmailScreen
import com.swetdot.swetgram.ui.legal.LegalGateDestination
import com.swetdot.swetgram.ui.legal.LegalScreen
import com.swetdot.swetgram.ui.legal.LegalStatusScreen
import com.swetdot.swetgram.ui.legal.LegalViewModel
import com.swetdot.swetgram.ui.legal.gateDestination
import com.swetdot.swetgram.ui.theme.SwetgramTheme

/**
 * The gate between signed-out, awaiting-verification, and signed-in.
 *
 * THREE states, not two. Registration RETURNS a session, so "has tokens" does not mean "may
 * use the app" -- the contract requires a verified email before a user can send anything. A
 * binary gate sends a freshly registered user straight to a chat list that will reject their
 * first message, and the code they were emailed has nowhere to go.
 *
 * Driven by persisted state, so closing the app mid-signup returns to the code screen rather
 * than losing the step, and a session revoked mid-use drops back to sign-in.
 */
@Composable
fun App(container: AppContainer) {
    // BEFORE anything composes an image, and before the theme: `SingletonImageLoader.get` is
    // called lazily by the first `rememberAsyncImagePainter` on screen, and whichever loader is
    // installed at that moment is the one the whole process keeps. Installing it here rather than
    // inside a `LaunchedEffect` is Coil's own instruction -- an effect runs after the first
    // composition, by which time the default loader has already been created, with an HttpClient
    // of Coil's own making rather than this app's engine.
    setSingletonImageLoaderFactory { context -> swetImageLoader(context, container.imageHttp) }

    val session by container.session.state.collectAsStateWithLifecycle()

    SwetgramTheme {
        val current = session
        when {
            current == null -> {
                val viewModel = viewModel { AuthViewModel(container.auth, container.session) }
                AuthFlow(viewModel)
            }

            !current.emailVerified -> {
                val viewModel = viewModel { AuthViewModel(container.auth, container.session) }
                val state by viewModel.state.collectAsStateWithLifecycle()
                VerifyEmailScreen(
                    state = state,
                    email = current.email,
                    onVerify = viewModel::verifyEmail,
                    onResend = viewModel::resendVerification,
                    // Without an escape a user who mistyped their address is stuck on this
                    // screen forever, with no way back to registration.
                    onSignOut = viewModel::signOut,
                )
            }

            else -> {
                val legalViewModel = viewModel(key = "legal:${current.userId}") {
                    LegalViewModel(container.legal, container.auth, container.profiles)
                }
                val legalState by legalViewModel.state.collectAsStateWithLifecycle()
                when (legalState.gateDestination()) {
                    LegalGateDestination.CHECKING -> LegalStatusScreen(
                        state = legalState,
                        onRetry = legalViewModel::refresh,
                        onSignOut = legalViewModel::signOut,
                        onRequestDeletion = legalViewModel::requestDeletion,
                        onDismissError = legalViewModel::dismissError,
                    )

                    LegalGateDestination.ACCEPTANCE -> LegalScreen(
                        state = legalState,
                        onAccept = legalViewModel::accept,
                        onRetry = legalViewModel::refresh,
                        onSignOut = legalViewModel::signOut,
                        onRequestDeletion = legalViewModel::requestDeletion,
                        onDismissError = legalViewModel::dismissError,
                    )

                    LegalGateDestination.APP -> {
                        // Keyed on the user id, NOT on the session: the session object is replaced
                        // on every token refresh, which would tear the socket down and rebuild it
                        // every fifteen minutes for no reason.
                        LaunchedEffect(current.userId) {
                            container.realtime.start(this)
                            // Registered here rather than at sign-in so a token that rotated while
                            // the app was closed is refreshed on the next launch -- FCM does not
                            // tell a stopped app that its token changed.
                            runCatching { container.pushRegistrar?.registerCurrentDevice() }
                            // Declaring presence is what makes any of it real. The server serves
                            // POST /presence, leases it, sweeps it and renders is_online from it --
                            // but nothing was calling this, so every user was permanently offline,
                            // the green dot could never appear, and the sweeper looped over an empty
                            // table. Swallowed like the registration beside it: presence failing is
                            // never a reason to keep someone out of the app.
                            runCatching { container.privacy.goOnline() }
                        }

                        // The other half. Without it the app only ever says "online" and relies on
                        // the lease expiring, so `last_seen_at` is never written on a clean
                        // backgrounding and a peer sees a stale green dot for the whole lease.
                        DisposableEffect(current.userId) {
                            onDispose {
                                // onDispose is not a coroutine scope, and the caller's scope is
                                // being torn down with it -- so this needs its own, deliberately not
                                // tied to the composition it is reacting to.
                                CoroutineScope(SupervisorJob() + ioDispatcher).launch {
                                    runCatching { container.privacy.goOffline() }
                                }
                            }
                        }
                        SwetgramApp(container)
                    }
                }
            }
        }
    }
}
